import React, { useEffect, useMemo, useRef, useState, ForwardedRef } from 'react';
import { Stage, Layer, Image as KonvaImage, Line, Text } from 'react-konva';
import useImage from 'use-image';
import Konva from 'konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import KonvaFogLayerContainer from './KonvaFogLayerContainer';
import KonvaMarkersLayerContainer from './KonvaMarkersLayerContainer';
import RegionBordersLayerContainer from './RegionBordersLayerContainer';
import KonvaDrawingsLayerContainer from './KonvaDrawingsLayerContainer';
import KonvaInteractionLayerContainer from './KonvaInteractionLayerContainer';
import type { InteractionState } from '../../types/types.ts';
import { useGridCalculator } from '../../hooks/useGridCalculator';
import { useMapInteraction } from '../../hooks/useMapInteraction';
import { useGrid } from '../GridProvider/useGrid';
import { logger } from '../../services/logger.client';
import { useStableCallback, useRenderLoopDetection } from '../../hooks/hookUtils';

/**
 * Represents the current viewport transform of the Konva stage.
 * Used to trigger grid recalculation when the user pans or zooms.
 * We track this in state because:
 * 1. Grid lines/labels need to recalculate when the visible viewport changes
 * 2. The stage ref contains the transform values, but ref changes don't trigger re-renders
 * 3. By updating state on pan/zoom completion, we get controlled grid updates
 */
interface StageTransform {
  x: number;
  y: number;
  scale: number;
}

interface RenderedImageRect {
  x: number;
  y: number;
  width: number;
  height: number;
}
interface KonvaMapProps {
  containerSize: { width: number; height: number; } | null;
  imageUrl: string | null;
  onImageRectChange: (rect: RenderedImageRect | null) => void;
  drawingMode: string | null;
  setDrawingMode: (mode: string | null) => void;
}
interface KonvaMapRef {
  getStage: () => Konva.Stage;
  resetView: () => void;
  fitImage: () => void;
}

const KonvaMap = React.forwardRef<KonvaMapRef, KonvaMapProps>((props: KonvaMapProps, ref: ForwardedRef<KonvaMapRef>) => {
  // Development-time render loop detection for this complex component
  useRenderLoopDetection('KonvaMap');

  // Refs
  const { containerSize, imageUrl, onImageRectChange, drawingMode, setDrawingMode } = props;
  const stageRef = useRef<Konva.Stage>(null);
  const isDrawingRef = useRef(false);

  const isPanning = !drawingMode;

  // Track stage transform to trigger grid recalculation on pan/zoom.
  // This state is updated when:
  // 1. User finishes dragging (panning) the stage
  // 2. User zooms with the mouse wheel
  // 3. fitImage() or resetView() are called
  // The gridLines and gridLabels useMemo hooks depend on this state.
  const [stageTransform, setStageTransform] = useState<StageTransform>({ x: 0, y: 0, scale: 1 });

  // The use-image hook simplifies loading an image for Konva.
  // It handles the `new window.Image()` and `img.onload` logic for us.
  const [image] = useImage(imageUrl ?? '', 'anonymous');

  // Calculate image dimensions to fit and center within the container.
  const imageRect = React.useMemo(() => {
    if (!image || !containerSize) {
      return null;
    }

    const containerRatio = containerSize.width / containerSize.height;
    const imageRatio = image.width / image.height;

    let width, height;
    if (containerRatio > imageRatio) {
      // Container is wider than the image, so height is the constraint.
      height = containerSize.height;
      width = height * imageRatio;
    } else {
      // Container is taller or same ratio, so width is the constraint.
      width = containerSize.width;
      height = width / imageRatio;
    }

    // Calculate the top-left position to center the image.
    const x = 0;
    const y = 0;

    return { x, y, width, height };
  }, [image, containerSize]);

  const { gridSettings, getCellFromCoordinates, getCoordsInPercent } = useGridCalculator({ imageRect });
  const { gridOrigin, gridStrategy } = useGrid();
  
  // Inform the parent component about the calculated image rectangle
  useEffect(() => {
    onImageRectChange(imageRect);
  }, [imageRect, onImageRectChange]);

  const {    interaction,
    hoveredCell,
    setHoveredCell,
    tooltipState,
    setTooltipState,
    saveRegion,
    lastMousePositionRef,
    setInteraction,
    occupiedCells,
    finishDrawing,
  } = useMapInteraction({
    drawingMode,
    setDrawingMode,
    stageRef,
    imageRect,
    gridSettings,
    getCoordsInPercent,
  });

  // useStableCallback ensures this helper always has access to the current stageRef
  // without needing to be recreated. Since it only reads from a ref, the stable reference
  // prevents unnecessary recreation of dependent callbacks.
  const getTransformedPointerPosition = useStableCallback(() => {
    const stage = stageRef.current;
    if (!stage) return null;
    const pointer = stage.getPointerPosition();
    if (!pointer) return null;
    const transform = stage.getAbsoluteTransform().copy().invert();
    return transform.point(pointer);
  });

  // useStableCallback provides a stable reference that always accesses the latest values
  // of drawingMode, getCellFromCoordinates, occupiedCells, and setInteraction. This prevents
  // the handler from being recreated on every render when these dependencies change,
  // which is important for Konva event binding performance.
  const handleMouseDown = useStableCallback((e: KonvaEventObject<MouseEvent>) => {  
    if (e.evt.button !== 0 || !drawingMode) return;

    const transformedPos = getTransformedPointerPosition();
    if (!transformedPos) return;

    isDrawingRef.current = true;

    switch (drawingMode) {
      case 'line':
      case 'journey':
        setInteraction({ type: 'line', points: [transformedPos] });
        break;
      case 'rect':
      case 'circle':
        setInteraction({ type: 'shape', shape: drawingMode, start: transformedPos, current: transformedPos });
        break;
      case 'region': {
        const cell = getCellFromCoordinates(transformedPos.x, transformedPos.y);
        if (cell) {
          const cellKey = `${cell.row}-${cell.col}`;
          if (occupiedCells.has(cellKey)) {
            logger.warn({ cellKey }, 'Attempted to start region paint on an already occupied cell.');
            isDrawingRef.current = false;
            return;
          }
          logger.debug({ pos: transformedPos, cell }, 'Region drawing started at cell.');
          setInteraction({ type: 'region', id: `region-${Date.now()}`, selectedCells: new Set([cellKey]) });
        }
        break;
      }
      case 'region-click': {
        const clickedCell = getCellFromCoordinates(transformedPos.x, transformedPos.y);
        if (clickedCell) {
          setInteraction((prev: InteractionState | null) => {
            const cellKey = `${clickedCell.row}-${clickedCell.col}`;
            if (occupiedCells.has(cellKey) && (prev?.type !== 'region' || !prev.selectedCells.has(cellKey))) {
              logger.warn({ cellKey }, 'Attempted to select an already occupied cell.');
              return prev;
            }
            if (prev?.type !== 'region') {
              logger.debug({ pos: transformedPos, cell: clickedCell }, 'Region click-drawing started at cell.');
              return { type: 'region', id: `region-${Date.now()}`, selectedCells: new Set([cellKey]) };
            } else {
              const newSelectedCells = new Set(prev.selectedCells);
              if (newSelectedCells.has(cellKey)) newSelectedCells.delete(cellKey); else newSelectedCells.add(cellKey);
              return { ...prev, selectedCells: newSelectedCells };
            }
          });
        }
        break;
      }
    }
  });

  // useStableCallback ensures the mouse move handler always has access to latest state
  // without triggering re-renders. This is critical for performance since mouse move
  // events fire rapidly during drawing operations.
  const handleMouseMove = useStableCallback((e: KonvaEventObject<MouseEvent>) => {
    const transformedPos = getTransformedPointerPosition();
    if (!transformedPos) return;

    const cell = getCellFromCoordinates(transformedPos.x, transformedPos.y);
    setHoveredCell(cell);
    lastMousePositionRef.current = { x: e.evt.clientX, y: e.evt.clientY };
    setTooltipState(s => ({ ...s, x: e.evt.clientX, y: e.evt.clientY, visible: s.visible, cell: cell }));

    if (!isDrawingRef.current) return;

    setInteraction((prev: InteractionState | null) => {
      if (!prev) return null;
      if (prev.type === 'region' && cell) {
        const cellKey = `${cell.row}-${cell.col}`;
        if (!prev.selectedCells.has(cellKey) && !occupiedCells.has(cellKey)) {
          const newCells = new Set(prev.selectedCells).add(cellKey);
          return { ...prev, selectedCells: newCells };
        }
      } else if (prev.type === 'shape') {
        return { ...prev, current: transformedPos };
      } else if (prev.type === 'line') {
        return { ...prev, points: [...prev.points, transformedPos] };
      }
      return prev;
    });
  });

  // useStableCallback ensures saveRegion is called with the latest interaction state.
  // This prevents stale closure issues where the handler might reference an outdated
  // interaction value from a previous render cycle.
  const handleMouseUp = useStableCallback(() => {
    isDrawingRef.current = false;
    if (interaction?.type === 'region' && drawingMode === 'region') { // Drag-to-paint region
      logger.debug({ interaction }, 'Region drag-drawing finished via mouse-up, opening save modal.');
      saveRegion();
    }
  });

  // useStableCallback provides stable reference while accessing latest drawingMode.
  // This is used for right-click context menu handling to finish line drawings.
  const handleContextMenu = useStableCallback((e: KonvaEventObject<PointerEvent>) => {
    if (drawingMode === 'line' || drawingMode === 'journey') {
      e.evt.preventDefault();
      finishDrawing();
    }
  });

  // Calculate visible stage bounds from the tracked transform state.
  // This is a pure calculation based on state values, not a ref read.
  // The useMemo hooks that depend on stageTransform will recalculate when pan/zoom changes.
  const getVisibleStageBounds = useMemo(() => {
    if (!containerSize) return { x1: 0, y1: 0, x2: 0, y2: 0 };
    const { x, y, scale } = stageTransform;
    return {
      x1: -x / scale,
      y1: -y / scale,
      x2: (-x + containerSize.width) / scale,
      y2: (-y + containerSize.height) / scale
    };
  }, [stageTransform, containerSize]);

  const gridLines = useMemo(() => {
    if (!gridSettings.enabled || !imageRect || !gridStrategy) {
      return [];
    }
    const bounds = {
      x1: gridSettings.clipToImage ? Math.max(imageRect.x, getVisibleStageBounds.x1) : getVisibleStageBounds.x1,
      y1: gridSettings.clipToImage ? Math.max(imageRect.y, getVisibleStageBounds.y1) : getVisibleStageBounds.y1,
      x2: gridSettings.clipToImage ? Math.min(imageRect.x + imageRect.width, getVisibleStageBounds.x2) : getVisibleStageBounds.x2,
      y2: gridSettings.clipToImage ? Math.min(imageRect.y + imageRect.height, getVisibleStageBounds.y2) : getVisibleStageBounds.y2,
    };
    return gridStrategy.getGridLines(bounds);
  }, [gridSettings, gridStrategy, imageRect, getVisibleStageBounds]);

  const gridLabels = useMemo(() => {
    if (!gridSettings.enabled || !gridSettings.showLabels || !imageRect || !gridStrategy) {
      return [];
    }
    return gridStrategy.getGridLabels(getVisibleStageBounds);
  }, [gridSettings, gridStrategy, imageRect, getVisibleStageBounds]);

  // useStableCallback provides a stable reference for fitImage that always accesses
  // the current containerSize and imageRect values. This is exposed via useImperativeHandle
  // for parent components and also used in the initial fit effect. Using useStableCallback
  // (rather than useCallback) ensures the function identity remains stable while still
  // accessing the latest state values.
  const fitImage = useStableCallback(() => {
    if (!containerSize || !imageRect?.width || !imageRect?.height) return;
    const scale = Math.min(containerSize.width / imageRect.width, containerSize.height / imageRect.height);
    if (stageRef.current) {
      stageRef.current.scale({ x: scale, y: scale });
      stageRef.current.position({ x: 0, y: 0 });
      // Update tracked transform state to trigger grid recalculation
      setStageTransform({ x: 0, y: 0, scale });
    }
  });

  // Expose methods to parent via ref
  React.useImperativeHandle(ref, () => ({
    // Expose the actual stage instance for event handling
    getStage: () => stageRef.current as Konva.Stage,
    // Expose custom methods
    resetView: () => {
      if (stageRef.current) {
        stageRef.current.scale({ x: 1, y: 1 });
        stageRef.current.position({ x: 0, y: 0 });
        // Update tracked transform state to trigger grid recalculation
        setStageTransform({ x: 0, y: 0, scale: 1 });
      }
    },
    fitImage: fitImage,
  }));
  
  // Fit the image when it changes. Since fitImage uses useStableCallback, it always
  // has access to the latest containerSize and imageRect values, so we only need
  // to trigger this effect when the image itself changes.
  useEffect(() => {
    fitImage();
  }, [fitImage, image]);  

  // useStableCallback provides a stable reference for the wheel zoom handler.
  // This handler reads scale and position directly from the Konva stage instance
  // and updates the tracked transform state to trigger grid recalculation.
  const handleWheel = useStableCallback((e: KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();

    const scaleBy = 1.05;
    const stage = e.target.getStage();
    if (!stage) return;

    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    // How much to zoom
    const newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;

    const newX = pointer.x - mousePointTo.x * newScale;
    const newY = pointer.y - mousePointTo.y * newScale;

    stage.scale({ x: newScale, y: newScale });
    stage.position({ x: newX, y: newY });

    // Update tracked transform state to trigger grid recalculation
    setStageTransform({ x: newX, y: newY, scale: newScale });
  });

  // useStableCallback provides a stable reference for the drag end handler.
  // This is called when the user finishes panning the stage by dragging.
  // We update the tracked transform state to trigger grid recalculation.
  const handleDragEnd = useStableCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const { x, y } = stage.position();
    const scale = stage.scaleX();
    setStageTransform({ x, y, scale });
  });

  // Crucial: Do not render the stage until the image and container are ready.
  if (!imageRect) {
    return <div>Calculating map layout...</div>;
  }

  if (!containerSize) {
    return <div>Loading map container...</div>;
  }

  return (
    <Stage
      ref={stageRef}
      width={containerSize.width}
      height={containerSize.height}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onContextMenu={handleContextMenu}
      onDragEnd={handleDragEnd}
      onMouseLeave={() => {
        setTooltipState((s: { x: number; y: number; visible: boolean; cell: { row: number; col: number; } | null; }) => ({ ...s, visible: false, cell: null }));
        setHoveredCell(null);
        handleMouseUp(); // Also treat leaving the stage as a mouse up event
      }}
      draggable={isPanning} // Konva handles internal drag state; we track position via onDragEnd
      style={{ cursor: drawingMode ? 'crosshair' : 'grab' }}
    >
      <Layer>
        {imageRect && <KonvaImage image={image} {...imageRect} />}
      </Layer>
      {gridSettings.enabled && (
        <Layer>
          {gridLines.map((line: { key: string; points: number[] }) => (
            <Line
              key={line.key}
              points={line.points}
              stroke={`rgba(255, 255, 255, ${gridSettings.opacity})`}
              strokeWidth={1}
            />
          ))}
        </Layer>
      )}
      {gridSettings.enabled && gridSettings.showLabels && (
        <Layer listening={false}>
          {gridLabels.map((label: { key: string; x: number; y: number; text: string }) => (
            <Text
              key={label.key}
              x={label.x}
              y={label.y}
              text={label.text}
              fontSize={12 / (stageRef.current?.scaleX() ?? 1)} // Keep font size consistent on zoom
              fill={`rgba(255, 255, 255, ${gridSettings.opacity})`}
              align="center"
              verticalAlign="middle"
            />
          ))}
        </Layer>
      )}
      {/* Combined Layer for dynamic elements */}
      <Layer>
        <KonvaDrawingsLayerContainer imageRect={imageRect} interaction={interaction} />
        <RegionBordersLayerContainer imageRect={imageRect} stageRef={stageRef} />
        <KonvaInteractionLayerContainer
          imageRect={imageRect}
          interaction={interaction}
          drawingMode={drawingMode}
          hoveredCell={hoveredCell}
          tooltipState={tooltipState}
          gridOrigin={gridOrigin}
          stageRef={stageRef}
          saveRegion={saveRegion}
        />
        {imageRect && <KonvaMarkersLayerContainer imageRect={imageRect} />}
      </Layer>
      {imageRect && <KonvaFogLayerContainer imageRect={imageRect} containerSize={containerSize} />}
    </Stage>
  );
});

export default KonvaMap;